import * as React from 'react';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { DeepReadonly } from 'utility-types';
import { InlineBadge } from '../InlineBadge';
import { NavLinkListItem } from './NavLinkListItem';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { SectionContainer } from '../../containers/Nav/Section';
import { useTranslation } from 'react-i18next';

const { AionReportingUI, AionOshaViolationsUI } = AppName;
const { Read } = ActivityAction;
const { OshaViolations } = ActivityResourceName;

const {
  FlexTrack,
  IncidentReports,
  RegionMetrics,
  RegionMetricsWithoutTotal,
  OperationalMetrics
} = OrganizationFeature;

export type Props = {
  features: DeepReadonly<OrganizationFeature[]>;
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  organizationId: string;
  flexTrackCount: number | null;
  operationalReportingCount: number | null;
};

export const ReportingSection: React.FC<Props> = ({
  features,
  organizationId,
  flexTrackCount,
  operationalReportingCount,
  hasGlobalPermission,
  hasOrganizationPermission
}) => {
  const hasFlexTrackFeature =
    features.includes(FlexTrack) || features.includes(RegionMetrics) || features.includes(RegionMetricsWithoutTotal);
  const hasOperationsFeature = features.includes(OperationalMetrics);
  const { t } = useTranslation();

  return (
    <SectionContainer
      id={AionReportingUI}
      subheader={t('components.reportingSection.reporting', 'Reporting')}
      icon={<EqualizerIcon />}
      hasNotifications={Boolean(
        (hasFlexTrackFeature && flexTrackCount) || (hasOperationsFeature && operationalReportingCount)
      )}
    >
      {hasFlexTrackFeature &&
        (process.env.REACT_APP_NAME === AionReportingUI ? (
          <NavLinkListItem inset to={`/${organizationId}/reporting/regional/clients`}>
            <InlineBadge badgeContent={flexTrackCount}>{t('components.common.flexTrack', 'FlexTrack')}</InlineBadge>
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/reporting/regional/clients`}>
            <InlineBadge badgeContent={flexTrackCount}>{t('components.common.flexTrack', 'FlexTrack')}</InlineBadge>
          </AnchorListItem>
        ))}
      {features.includes(IncidentReports) &&
        (process.env.REACT_APP_NAME === AionReportingUI ? (
          <NavLinkListItem inset to={`/${organizationId}/reporting/incidents`}>
            {t('components.reportingSection.leadingIndicators', 'Leading Indicators')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/reporting/incidents`}>
            {t('components.reportingSection.leadingIndicators', 'Leading Indicators')}
          </AnchorListItem>
        ))}
      {hasOperationsFeature &&
        (process.env.REACT_APP_NAME === AionReportingUI ? (
          <NavLinkListItem inset to={`/${organizationId}/reporting/operations/clients`}>
            <InlineBadge badgeContent={operationalReportingCount}>
              {t('components.reportingSection.operations', 'Operations')}
            </InlineBadge>
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/reporting/operations/clients`}>
            <InlineBadge badgeContent={operationalReportingCount}>
              {t('components.reportingSection.operations', 'Operations')}
            </InlineBadge>
          </AnchorListItem>
        ))}
      <AnchorListItem inset href="/SSQV4/Reports/UserPasswordReset.aspx">
        {t('components.reportingSection.passwordAuditReport', 'Password Audit Report')}
      </AnchorListItem>
      {(hasOrganizationPermission(Read, OshaViolations) || hasGlobalPermission(Read, OshaViolations)) &&
        (process.env.REACT_APP_NAME === AionOshaViolationsUI ? (
          <NavLinkListItem exact inset to={`/organizations/${organizationId}/osha-violations`}>
            {t('components.reportingSection.oshaViolations', 'OSHA Violations')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/osha-violations`}>
            {t('components.reportingSection.oshaViolations', 'OSHA Violations')}
          </AnchorListItem>
        ))}
    </SectionContainer>
  );
};
